import { IconButton, Stack } from "@fluentui/react";
import React, { Fragment, ReactNodeArray } from "react";

import { range } from "../../utils/range/range";
import { DefaultButton } from "../inputs/button/DefaultButton";
import { PrimaryButton } from "../inputs/button/PrimaryButton";
import { Dropdown } from "../inputs/dropdown/Dropdown";
import { Text } from "../items/Text";

export interface ListTablePaginationProps {
    currentPage: number;
    totalItemsCount: number;
    pageSize: number;
    onPageChange(page: number): void;
    onPageSizeChange?(pageSize: number): void;
}

export interface ListTablePaginationFilters {
    page: number;
    pageSize: number;
}

export const ListTablePagination: React.FunctionComponent<ListTablePaginationProps> = (
    props: ListTablePaginationProps
) => {
    if (!props.totalItemsCount) {
        return null;
    }

    const totalPagesCount = Math.ceil(props.totalItemsCount / props.pageSize);

    const pageNumbers = getPageNumbers(props.currentPage, totalPagesCount);

    const paginationElements: ReactNodeArray = pageNumbers.map((pageNumber: number) =>
        getPaginationElements(pageNumber, props.currentPage, () => props.onPageChange(pageNumber))
    );

    const isLastPage = props.currentPage === totalPagesCount;

    const firstDisplayedItemIndex = (props.currentPage - 1) * props.pageSize + 1;
    const lastDisplayedItemIndex = isLastPage ? props.totalItemsCount : props.currentPage * props.pageSize;

    const onPreviousClick = (): void => {
        props.onPageChange(props.currentPage - 1);
    };

    const onNextClick = (): void => {
        props.onPageChange(props.currentPage + 1);
    };

    const onChange = (key: string): void => {
        props.onPageSizeChange?.(Number(key));
    };

    return (
        <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="center">
            <Stack data-testId="table-pagination-showing" horizontal={true}>
                <Text>{`Showing ${firstDisplayedItemIndex} to ${lastDisplayedItemIndex} of`} </Text>
                <Fragment>&nbsp;</Fragment>
                <Text testId="table-pagination-total-count">{props.totalItemsCount}</Text>
            </Stack>
            <Stack horizontal={true} verticalAlign="center" tokens={{ childrenGap: 10, padding: 3 }}>
                {props.onPageSizeChange && (
                    <>
                        <Text>Display</Text>
                        <Dropdown
                            data-testid="table-pagination-pagesize"
                            options={[
                                { key: "10", text: "10" },
                                { key: "20", text: "20" },
                                { key: "50", text: "50" },
                                { key: "100", text: "100" }
                            ]}
                            onChange={onChange}
                            styles={{ dropdown: { width: 55 } }}
                            selectedKey={String(props.pageSize)}
                        />
                    </>
                )}
                <Stack horizontal={true} verticalAlign="center" tokens={{ childrenGap: 3 }}>
                    <IconButton
                        data-testid="table-pagination-prev"
                        iconProps={{ iconName: "ChevronLeft" }}
                        disabled={props.currentPage === 1}
                        onClick={onPreviousClick}
                    />
                    {paginationElements}
                    <IconButton
                        data-testid="table-pagination-next"
                        iconProps={{ iconName: "ChevronRight" }}
                        disabled={props.currentPage === totalPagesCount}
                        onClick={onNextClick}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

ListTablePagination.defaultProps = {
    pageSize: 50
};

const getPageNumbers = (currentPage: number, totalPagesCount: number): number[] => {
    const pageOffset = 2;

    let rangeStart = currentPage - pageOffset > 0 ? currentPage - pageOffset : 1;

    let rangeEnd = rangeStart + pageOffset * 2;

    if (rangeEnd > totalPagesCount) {
        rangeEnd = totalPagesCount;
        rangeStart = rangeEnd - pageOffset * 2;
        if (rangeStart < 1) {
            rangeStart = 1;
        }
    }

    return range(rangeStart, rangeEnd);
};

const getPaginationElements = (page: number, currentPage: number, onPageChange: (page: number) => void) => {
    const isActive = page === currentPage;

    const onClick = (): void => onPageChange(page);

    return isActive ? (
        <PrimaryButton
            key={String(page)}
            styles={{ root: { width: 30, minWidth: 30, pointerEvents: "none" } }}
            text={String(page)}
            onClick={onClick}
        />
    ) : (
        <DefaultButton key={String(page)} style={{ width: 30, minWidth: 30 }} text={String(page)} onClick={onClick} />
    );
};
